import { talentNetworkText } from "@/i18n/talentNetwork.constants";
import { CandidateEducationEditorSchema, CandidateExperienceEditorSchema, CandidateProjectEditorSchema } from "@artha/api-contracts";
import type { ComponentType } from "react";
import { Gitlab, Dribbble } from "lucide-react";
import LinkedInIcon from "@/components/icons/ShareSocialIcon/linkedInIcon";
import { FacebookIcon, InstagramIcon, XIcon, YouTubeIcon } from "@/components/icons/SocialIcons";
import {
  CANDIDATE_PROFILE_MAX_EDUCATION,
  CANDIDATE_PROFILE_MAX_EXPERIENCE,
  CANDIDATE_PROFILE_MAX_PROJECTS,
  CANDIDATE_PROFILE_DESCRIPTION_MAX_LENGTH,
  CANDIDATE_PROFILE_DATE_MAX_LENGTH,
  CANDIDATE_PROFILE_URL_MAX_LENGTH,
} from "@artha/api-contracts";
import type {
  ProfileHistoryConfig,
  ProfileHistoryField,
  ProfileHistoryKind,
} from "./profileCards.types";

export const PROFILE_CARD_CLASS_NAME =
  "break-words rounded-2xl border border-black bg-white p-4 shadow-[0px_4px_0px_0px_#000000]";
export const PROFILE_CARD_EDIT_CLASS_NAME =
  "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded text-[#0D41E1] transition-colors hover:bg-black/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0D41E1] focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-40";
export const PROFILE_CARD_ACTION_ICON_CLASS_NAME = "h-4 w-4 shrink-0";
export const PROFILE_CARD_ICON_CLASS_NAME =
  "flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-[#EAECF0] bg-[#F9FAFB] text-[#475467]";
export const PROFILE_EDITOR_CLOSE_CLASS_NAME =
  "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[#344054] hover:bg-black/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black";
export const PROFILE_CARD_INPUT_CLASS_NAME =
  "h-11 w-full rounded-lg border border-black bg-white px-3 text-sm text-[#101828] outline-none focus:ring-2 focus:ring-[#F8D96B]";
export const PROFILE_CARD_LABEL_CLASS_NAME =
  "mb-1.5 block text-xs font-semibold text-[#344054]";
export const PROFILE_CHIP_CLASS_NAME =
  "max-w-full [&>span]:min-w-0 [&>span]:break-words [&>button]:shrink-0";
export const PROFILE_CARD_VISIBLE_ENTRIES = 3;
export const PROFILE_DESCRIPTION_VISIBLE_LINES = 3;
export const PROFILE_CARD_MAX_CHIPS = 100;
export const PROFILE_CARD_CHIP_MAX_LENGTH = 120;
export const PROFILE_CARD_DATE_PATTERN =
  /^(\d{4})-(\d{1,2})(?:-\d{1,2}(?:T.*)?)?$/;
export const PROFILE_CARD_REQUIRED_PATTERN = ".*\\S.*";
export const PROFILE_CARD_TITLES_ERROR =
  talentNetworkText("job_titles_error");
export const PROFILE_CARD_HTTP_PATTERN = /^https?:\/\//i;
export const PROFILE_CARD_HISTORY_KINDS = [
  "experience",
  "education",
  "projects",
] as const;
export const PROFILE_CARD_CHIP_KINDS = ["skills"] as const;
export const PROFILE_CARD_DATE_FIELDS: readonly ProfileHistoryField[] = [
  {
    key: "startDate",
    label: talentNetworkText("start_date"),
    inputType: "month",
    maxLength: CANDIDATE_PROFILE_DATE_MAX_LENGTH,
  },
  {
    key: "endDate",
    label: talentNetworkText("end_date"),
    inputType: "month",
    maxLength: CANDIDATE_PROFILE_DATE_MAX_LENGTH,
  },
];
export const PROFILE_CARD_DESCRIPTION_FIELD: ProfileHistoryField = {
  key: "description",
  label: talentNetworkText("description"),
  multiline: true,
  maxLength: CANDIDATE_PROFILE_DESCRIPTION_MAX_LENGTH,
};
export const PROFILE_HISTORY_CONFIG = {
  experience: {
    title: talentNetworkText("experience_title"),
    singular: "experience",
    plural: talentNetworkText("roles"),
    limit: CANDIDATE_PROFILE_MAX_EXPERIENCE,
    primary: "title",
    secondary: "company",
    currentLabel: talentNetworkText("current_job"),
    fields: [
      { key: "title", label: talentNetworkText("job_title"), required: true, maxLength: 120 },
      { key: "company", label: talentNetworkText("company"), maxLength: 200 },
      {
        key: "employmentType",
        label: talentNetworkText("employment_type"),
        maxLength: 80,
      },
      ...PROFILE_CARD_DATE_FIELDS,
      { key: "location", label: talentNetworkText("location"), maxLength: 240 },
      PROFILE_CARD_DESCRIPTION_FIELD,
    ],
  },
  education: {
    title: talentNetworkText("education_title"),
    singular: "education",
    plural: talentNetworkText("education_records"),
    limit: CANDIDATE_PROFILE_MAX_EDUCATION,
    primary: "institution",
    secondary: "field",
    currentLabel: talentNetworkText("current_education"),
    fields: [
      {
        key: "institution",
        label: talentNetworkText("institute"),
        required: true,
        maxLength: 200,
      },
      { key: "level", label: talentNetworkText("education_level"), maxLength: 120 },
      { key: "field", label: talentNetworkText("specialization"), maxLength: 160 },
      ...PROFILE_CARD_DATE_FIELDS,
      { key: "grade", label: talentNetworkText("grade_label"), maxLength: 120 },
      PROFILE_CARD_DESCRIPTION_FIELD,
    ],
  },
  projects: {
    title: talentNetworkText("projects_title"),
    singular: "project",
    plural: "projects",
    limit: CANDIDATE_PROFILE_MAX_PROJECTS,
    primary: "name",
    secondary: "organization",
    currentLabel: talentNetworkText("current_project"),
    fields: [
      { key: "name", label: talentNetworkText("project_name"), required: true, maxLength: 200 },
      {
        key: "organization",
        label: talentNetworkText("associated_organization"),
        maxLength: 200,
      },
      ...PROFILE_CARD_DATE_FIELDS,
      PROFILE_CARD_DESCRIPTION_FIELD,
      {
        key: "url",
        label: talentNetworkText("project_link"),
        placeholder: "https://example.com",
        maxLength: CANDIDATE_PROFILE_URL_MAX_LENGTH,
      },
    ],
  },
} as const satisfies Record<ProfileHistoryKind, ProfileHistoryConfig>;

export const PROFILE_BASICS_FIELDS = [
  { key: "name", label: talentNetworkText("name"), type: "text", required: true, maxLength: 120 },
  {
    key: "email",
    label: talentNetworkText("email"),
    type: "email",
    required: true,
    maxLength: 254,
  },
  {
    key: "titles",
    label: talentNetworkText("job_titles"),
    type: "text",
    placeholder: talentNetworkText("job_titles_placeholder"),
    maxLength: 3025,
  },
  { key: "experienceYears", label: talentNetworkText("experience_years"), type: "number" },
] as const;
export const PROFILE_CONTACT_VERIFY_CLASS_NAME = "shrink-0 rounded px-1 text-xs font-semibold text-[#0D41E1] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0D41E1] disabled:opacity-50";
export const PROFILE_CONTACT_VERIFIED_CLASS_NAME = "inline-flex shrink-0 items-center text-green-600";

export const PROFILE_EMPLOYMENT_TYPES = [
  "Full-time", "Part-time", "Self-employed", "Freelance", "Contract", "Internship", "Apprenticeship", "Seasonal",
] as const;
export const PROFILE_DEFAULT_EMPLOYMENT_TYPE = PROFILE_EMPLOYMENT_TYPES[0];
export const PROFILE_CURRENT_DATE_PATTERN = /^(present|current|currently|ongoing|now|till date|to date)$/i;
export const PROFILE_YEAR_PATTERN = /^\d{4}$/;

export const PROFILE_LINK_LABELS: Record<string, string> = {
  "linkedin.com": "LinkedIn", "github.com": "GitHub", "hackerrank.com": "HackerRank",
  "leetcode.com": "LeetCode", "behance.net": "Behance", "dribbble.com": "Dribbble",
};

export const PROFILE_LINK_ICONS: Record<string, ComponentType<{ className?: string }> | string> = {
  "linkedin.com": LinkedInIcon,
  "github.com": "/socials/github.svg",
  "gist.github.com": "/socials/github.svg",
  "hackerrank.com": "/socials/hackerrank.svg",
  "gitlab.com": Gitlab,
  "dribbble.com": Dribbble,
  "facebook.com": FacebookIcon,
  "instagram.com": InstagramIcon,
  "twitter.com": XIcon,
  "x.com": XIcon,
  "youtube.com": YouTubeIcon,
  "youtu.be": YouTubeIcon,
};

export const PROFILE_HISTORY_EDITOR_SCHEMAS = { education: CandidateEducationEditorSchema, experience: CandidateExperienceEditorSchema, project: CandidateProjectEditorSchema };
